<script setup>
import WebsiteActionsCell from "@frontJs/components/tables/cells/projectManagement/dev/WebsiteActionsCell.vue";
import { ExclamationTriangleIcon, ShieldCheckIcon } from "@heroicons/vue/24/solid";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CrossIconCell from "@js/components/tables/cells/CrossIconCell.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { formatDate } from "@js/utils/formatter";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";

const { t } = useI18n();

const phpVersionMaintainedUntilDates = [
  {
    version: "5.4",
    maintainedUntil: "2015-09-03"
  },
  {
    version: "5.6",
    maintainedUntil: "2018-12-31"
  },
  {
    version: "7.0",
    maintainedUntil: "2019-01-10"
  },
  {
    version: "7.1",
    maintainedUntil: "2019-12-01"
  },
  {
    version: "7.2",
    maintainedUntil: "2020-11-30"
  },
  {
    version: "7.3",
    maintainedUntil: "2021-12-06"
  },
  {
    version: "7.4",
    maintainedUntil: "2022-11-28"
  },
  {
    version: "8.0",
    maintainedUntil: "2023-11-26"
  },
  {
    version: "8.1",
    maintainedUntil: "2025-12-31"
  },
  {
    version: "8.2",
    maintainedUntil: "2026-12-31"
  },
  {
    version: "8.3",
    maintainedUntil: "2027-12-31"
  },
  {
    version: "8.4",
    maintainedUntil: "2028-12-31"
  }
];

const { userSessionKey, cms } = defineProps({
  userSessionKey: {
    type: String,
    required: false
  },
  cms: {
    type: String,
    required: false
  }
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("label.name"),
      cell: (props) =>
        h(LinkCellV2, {
          text: props.row.original.name,
          href: route("front.projectManagement.dev.website", {
            tenant: props.row.original.id
          }),
          inertia: true
        })
    }),
    columnHelper.accessor(() => null, {
      id: "cmsVersion",
      header: () => t("label.cmsVersion"),
      cell: (props) =>
        props.row.original.cmsVersion
          ? h(TextCellV2, {
            text: props.row.original.cmsVersion
          })
          : h(CrossIconCell)
    }),
    columnHelper.accessor(() => null, {
      id: "cmsVersionMaintainedUntil",
      header: () => t("label.cmsVersionMaintainedUntil"),
      cell: (props) =>
        props.row.original.cmsVersionMaintainedUntil
          ? h(TextCellV2, {
            text: formatDate(props.row.original.cmsVersionMaintainedUntil),
            color:
              props.row.original.isCmsVersionMaintained === false
                ? "red"
                : null,
            tooltipContent: t("text.lastCurrentCmsVersion", {
              version: props.row.original.lastCurrentCmsVersion
            }),
            tooltipPlacement: "top",
            tooltipIcon: ExclamationTriangleIcon,
            tooltipColor:
              props.row.original.isCmsVersionMaintained === false &&
              props.row.original.isUpToDateOnCurrentCmsVersion === false
                ? "red"
                : props.row.original.isCmsVersionMaintained === false &&
                props.row.original.isUpToDateOnCurrentCmsVersion === true
                  ? "orange"
                  : props.row.original.isCmsVersionMaintained === true &&
                  props.row.original.isUpToDateOnCurrentCmsVersion ===
                  false
                    ? "yellow"
                    : props.row.original.isCmsVersionMaintained === true &&
                    props.row.original.isUpToDateOnCurrentCmsVersion ===
                    true
                      ? "green"
                      : null
          })
          : h(CrossIconCell)
    }),
    columnHelper.accessor(() => null, {
      id: "phpVersion",
      header: () => t("label.phpVersion"),
      cell: (props) =>
        props.row.original.phpVersion
          ? h(TextCellV2, {
            text: props.row.original.phpVersion
          })
          : h(CrossIconCell)
    }),
    columnHelper.accessor(() => null, {
      id: "phpVersion",
      header: () => t("label.phpVersionMaintainedUntil"),
      cell: (props) =>
        props.row.original.phpVersion
          ? h(TextCellV2, {
            text: formatDate(
              phpVersionMaintainedUntilDates.find(
                (item) => item.version === props.row.original.phpVersion
              ).maintainedUntil
            ),
            tooltipContent:
              new Date(
                phpVersionMaintainedUntilDates.find(
                  (item) => item.version === props.row.original.phpVersion
                ).maintainedUntil
              ) < new Date()
                ? t("text.phpVersionNotMaintained", {
                  version: props.row.original.phpVersion
                })
                : null,
            tooltipIcon: ExclamationTriangleIcon,
            tooltipPlacement: "top",
            tooltipColor: "red"
          })
          : h(CrossIconCell)
    }),
    columnHelper.accessor(() => null, {
      id: "twoFactorAuthenticationEnabled",
      header: () => t("label.twoFactorAuthenticationEnabled"),
      cell: (props) =>
        props.row.original.twoFactorAuthenticationEnabled
          ? h(ShieldCheckIcon, {
            class: "h-[52px] text-green-500 p-4"
          })
          : h(CrossIconCell)
    }),
    columnHelper.accessor(() => null, {
      id: "cspEnabled",
      header: () => t("label.cspEnabled"),
      cell: (props) =>
        props.row.original.cspEnabled
          ? h(ShieldCheckIcon, {
            class: "h-[52px] text-green-500 p-4"
          })
          : h(CrossIconCell)
    }),
    columnHelper.accessor(() => null, {
      id: "cloudflareEnabled",
      header: () => t("label.cloudflare"),
      cell: (props) =>
        props.row.original.cloudflareEnabled
          ? h(ShieldCheckIcon, {
            class: "h-[52px] text-green-500 p-4"
          })
          : h(CrossIconCell)
    }),
    columnHelper.accessor(() => null, {
      id: "paymentModules",
      header: () => t("label.paymentModules"),
      cell: (props) =>
        props.row.original.paymentModules.length > 0
          ? h(
            "ul",
            {
              class: "p-4 pl-8 list-disc",
            },
            props.row.original.paymentModules.map((module) =>
              h("li", { innerText: ` ${module.name} (${module.version ? module.version : t('text.unknownVersion')})` }),
            ),
          )
          : h(CrossIconCell),
      enableSorting: false
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      cell: (innerProps) =>
        h(WebsiteActionsCell, {
          row: innerProps.row
        })
    })
  ],
  url: route("front.projectManagement.dev.postWebsites"),
  data: {
    cms: cms
  },
  userSessionKey: userSessionKey,
  columnPinning: {
    right: ["actions"]
  }
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <DataTableLayout
    v-else
    :table="table"
    :count="count"
    :global-filter="globalFilter"
    :set-global-filter="setGlobalFilter"
    :available-filters="availableFilters"
    :filters="filters"
    :set-filters="setFilters"
  />
</template>
